import { AxiosError } from "axios";

const ERRORS = Object.freeze({
    SLACK_WORKSPACE_AUTH_ERROR: 69,
    SLACK_WORKSPACE_REGISTRATION_ERROR: 70,
    SLACK_CHANNEL_CREATION_ERROR: 71,
    SLACK_CHANNEL_INVITE_ERROR: 72,
    SLACK_WORKSPACE_LOGO_ERROR: 73,
    SLACK_CHANNEL_LEAVE_ERROR: 74,
    SLACK_WORKSPACE_LEAVE_ERROR: 75,
    SLACK_WORKSPACE_NOT_FOUND_ERROR: 76,
    SLACK_INSTALLATION_STORE_ERROR: 77,
    SLACK_INSTALLATION_FETCH_ERROR: 78,
    SLACK_INSTALLATION_DELETE_ERROR: 79,
    SLACK_WORKSPACE_DOCUMENT_NOT_FOUND_ERROR: 80,

    DISCORD_WORKSPACE_AUTH_ERROR: 169,
    DISCORD_CHANNEL_CREATION_ERROR: 170,
    DISCORD_WORKSPACE_REGISTRATION_ERROR: 171,

    STRIPE_FAILED_PAYMENT_ERROR: 569,
    STRIPE_UID_MISMATCH_ERROR: 570,

    WORKSPACE_REGISTRATION_NO_FREE_SUBSCRIPTION_ERROR: 1069,
    INVALID_AUTH_TOKEN_ERROR: 1070,
    //MISSING_PARAMETERS_ERROR: 1071,

    // CRITICAL ERRORS
    SUBSCRIPTION_VALIDATION_ERROR: 6969,
    //SUBSCRIPTION_REMOVAL_ERROR: 6970,
    NO_SUBSCRIPTION_FOUND_ERROR: 6971,
    //SUBSCRIPTION_ADDITION_ERROR: 6972,
});

/**
 * Catches any error and returns a tuple of [data, error]
 * @param {Function} asyncFn - Async function to execute
 * @param {...any} args - Arguments to pass to the async function
 * @returns {Promise<[any, Error|null]>} Tuple of [data, error]
 */
async function catchError(asyncFn, ...args) {
    try {
        const data = await asyncFn(...args);
        return [data, null];
    } catch (error) {
        return [null, error];
    }
}

/**
 * Catches any error and returns a tuple of [data, error, errorCode, errorMessage]
 * @param {Function} asyncFn - Async function to execute
 * @param {...any} args - Arguments to pass to the async function
 * @returns {Promise<[any, Error|null, string|null, string|null]>} Tuple of [data, error, errorCode, errorMessage]
 */
async function catchErrorParse(asyncFn, ...args) {
    try {
        const data = await asyncFn(...args);
        const { data: responseData } = data;
        if (!responseData) {
            throw new Error("No data returned from the server.");
        }
        const { errorCode, errorMessage } = responseData;
        if (errorCode || errorMessage) {
            return [null, new Error("Known error from server."), errorCode, errorMessage];
        }
        return [responseData, null, null, null];
    } catch (error) {
        if (error instanceof AxiosError) {
            return [null, error, error.response?.data?.errorCode, error.response?.data?.errorMessage];
        }
        return [null, error, null, null];
    }
}

async function catchErrorAuth(asyncFn, ...args) {
    try {
        const result = await asyncFn(...args);
        return [result, null, null];
    } catch (error) {
        let errorMessage;
        switch (error.code) {
            // Email/Password Errors
            case 'auth/email-already-in-use':
                errorMessage = "This email is already registered";
                break;
            case 'auth/invalid-email':
                errorMessage = "Invalid email format";
                break;
            case 'auth/weak-password':
                errorMessage = "Password is too weak";
                break;
            case 'auth/user-not-found':
                errorMessage = "No account found with this email";
                break;
            case 'auth/wrong-password':
                errorMessage = "Incorrect password";
                break;
            // Google Sign-In Errors    
            case 'auth/account-exists-with-different-credential':
                errorMessage = "Account exists with different sign-in method";
                break;
            case 'auth/operation-not-allowed':
                errorMessage = "This sign-in method is not enabled";
                break;
            // Phone Authentication Errors
            case 'auth/invalid-verification-code':
                errorMessage = "Invalid verification code";
                break;
            case 'auth/missing-verification-code':
                errorMessage = "Verification code required";
                break;
            case 'auth/missing-verification-id':
                errorMessage = "Verification ID missing";
                break;
            // General Errors
            case 'auth/network-request-failed':
                errorMessage = "Network error occurred";
                break;
            case 'auth/too-many-requests':
                errorMessage = "Too many attempts. Please try again later";
                break;
            default:
                console.log("Error:", error);
                errorMessage = "Authentication failed";
        }
        return [null, error?.code, errorMessage];
    }
}
export { catchError, catchErrorParse, ERRORS, catchErrorAuth };
